import Difficulty, { difficulties } from 'app/frontend/types/custom/Difficulty'
import {
  RaidPlanEncounterAssignmentAbility,
  RaidPlanTimeline,
} from 'app/frontend/types/custom/RaidPlan'
import VisualPlan, { VisualScreen } from 'app/frontend/types/custom/VisualPlan'
import { RaidPlanOwner } from 'app/frontend/base/api/raid_plans'
import {
  createVisualPlan,
  getVisualPlanIfPresent,
} from 'app/frontend/base/api/visual_plans'
import { decodeJsonBase64, encodeJsonBase64 } from './planStringTransfer'
import { isVisualScreen } from './visual_plans/visualPlanTransfer'

export type RaidPlanTransferVisual = {
  id: number
  ownerType: 'Team' | 'User'
  ownerId: number
  encounterId: number
  difficulty: Difficulty
  name: string
  screens: VisualScreen[]
}

export type RaidPlanTransferPayload = {
  timeline: RaidPlanTimeline
  message?: string
  visualPlans?: RaidPlanTransferVisual[]
}

export type RaidPlanImportSuccess = {
  ok: true
  timeline: RaidPlanTimeline
  message?: string
  visualPlans: RaidPlanTransferVisual[]
  actorCount: number
  castCount: number
  assignmentCount: number
  visualCount: number
}

export type RaidPlanImportFailure = {
  ok: false
  error: 'invalid'
}

export type RaidPlanImportResult = RaidPlanImportSuccess | RaidPlanImportFailure

export type RaidPlanTransferDestOwner = {
  ownerType: 'Team' | 'User'
  ownerId: number
}

type VisualPlanLookup = (
  owner: RaidPlanOwner,
  visualId: number,
) => Promise<VisualPlan | null>

type VisualPlanCreate = typeof createVisualPlan

const cloneTimeline = (timeline: RaidPlanTimeline): RaidPlanTimeline =>
  JSON.parse(JSON.stringify(timeline)) as RaidPlanTimeline

const countCasts = (timeline: RaidPlanTimeline): number => {
  const actorCasts = timeline.actors.reduce(
    (total, actor) => total + (actor.casts?.length ?? 0),
    0,
  )
  const generalCasts = timeline.general?.casts?.length ?? 0
  return actorCasts + generalCasts
}

const countAssignments = (timeline: RaidPlanTimeline): number =>
  Object.keys(timeline.encounter_assignments?.abilities ?? {}).length

export const raidPlanContentCounts = (timeline: RaidPlanTimeline) => ({
  actorCount: timeline.actors.length,
  castCount: countCasts(timeline),
  assignmentCount: countAssignments(timeline),
})

export const countRaidPlanContent = (timeline: RaidPlanTimeline): number =>
  timeline.actors.length + countCasts(timeline) + countAssignments(timeline)

export type RaidPlanTimelineMergeMode = 'all' | 'assignments' | 'cooldowns'

export const hasCustomRaidPlanContent = (
  timeline: RaidPlanTimeline,
): boolean => {
  const hasVisibleActorCasts = timeline.actors.some((actor) =>
    (actor.casts ?? []).some((cast) => !cast.hidden),
  )
  const hasVisibleGeneralCasts = (timeline.general?.casts ?? []).some(
    (cast) => !cast.hidden,
  )
  if (hasVisibleActorCasts || hasVisibleGeneralCasts) return true

  const abilities = timeline.encounter_assignments?.abilities ?? {}
  const snapshots = timeline.encounter_assignments?.default_snapshots ?? {}
  return Object.entries(abilities).some(([abilityKey, ability]) => {
    const snapshot = snapshots[abilityKey]
    if (!snapshot) return true
    return JSON.stringify(ability) !== JSON.stringify(snapshot)
  })
}

export const mergeRaidPlanTimeline = (
  current: RaidPlanTimeline,
  incoming: RaidPlanTimeline,
  mode: RaidPlanTimelineMergeMode,
): RaidPlanTimeline => {
  const nextCurrent = cloneTimeline(current)
  const nextIncoming = cloneTimeline(incoming)
  if (mode === 'all') return nextIncoming
  if (mode === 'assignments') {
    return {
      ...nextCurrent,
      encounter_assignments: nextIncoming.encounter_assignments,
    }
  }
  return {
    ...nextIncoming,
    phase_durations: nextCurrent.phase_durations,
    phase_order: nextCurrent.phase_order,
    encounter_assignments: nextCurrent.encounter_assignments,
  }
}

const isRaidPlanTimeline = (value: unknown): value is RaidPlanTimeline => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false
  }
  const timeline = value as Record<string, unknown>
  return Array.isArray(timeline.actors)
}

const isDifficulty = (value: unknown): value is Difficulty =>
  typeof value === 'string' &&
  (difficulties as readonly string[]).includes(value)

const isTransferOwnerType = (value: unknown): value is 'Team' | 'User' =>
  value === 'Team' || value === 'User'

const isRaidPlanTransferVisual = (
  value: unknown,
): value is RaidPlanTransferVisual => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false
  }
  const visual = value as Record<string, unknown>
  return (
    typeof visual.id === 'number' &&
    isTransferOwnerType(visual.ownerType) &&
    typeof visual.ownerId === 'number' &&
    typeof visual.encounterId === 'number' &&
    isDifficulty(visual.difficulty) &&
    typeof visual.name === 'string' &&
    Array.isArray(visual.screens) &&
    visual.screens.every(isVisualScreen)
  )
}

export const collectLinkedVisualIds = (
  timeline: RaidPlanTimeline,
): number[] => {
  const abilities = timeline.encounter_assignments?.abilities ?? {}
  const snapshots = timeline.encounter_assignments?.default_snapshots ?? {}
  const ids = new Set<number>()
  ;[abilities, snapshots].forEach((group) => {
    Object.values(group).forEach((ability) => {
      if (typeof ability.visual_id === 'number') ids.add(ability.visual_id)
    })
  })
  return [...ids]
}

export const toTransferVisual = (
  visual: VisualPlan,
): RaidPlanTransferVisual => ({
  id: visual.id,
  ownerType: visual.ownerType === 'User' ? 'User' : 'Team',
  ownerId: visual.ownerId,
  encounterId: visual.encounterId,
  difficulty: visual.difficulty,
  name: visual.name,
  screens: visual.screens,
})

export const encodeRaidPlanTransfer = (
  payload: RaidPlanTransferPayload,
): string => encodeJsonBase64(payload)

export const encodeRaidPlanTimeline = (
  timeline: RaidPlanTimeline,
  extras?: Pick<RaidPlanTransferPayload, 'message' | 'visualPlans'>,
): string => encodeRaidPlanTransfer({ timeline, ...extras })

export const parseRaidPlanImport = (input: string): RaidPlanImportResult => {
  const trimmed = input.trim()
  if (trimmed === '') {
    return { ok: false, error: 'invalid' }
  }

  try {
    const parsed = decodeJsonBase64(trimmed)

    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      return { ok: false, error: 'invalid' }
    }

    const record = parsed as Record<string, unknown>
    const timeline = record.timeline
    if (!isRaidPlanTimeline(timeline)) {
      return { ok: false, error: 'invalid' }
    }

    const message =
      typeof record.message === 'string' ? record.message : undefined
    const visualPlans = Array.isArray(record.visualPlans)
      ? record.visualPlans.filter(isRaidPlanTransferVisual)
      : []

    return {
      ok: true,
      timeline,
      message,
      visualPlans,
      actorCount: timeline.actors.length,
      castCount: countCasts(timeline),
      assignmentCount: countAssignments(timeline),
      visualCount: visualPlans.length,
    }
  } catch {
    return { ok: false, error: 'invalid' }
  }
}

const remapAbilityVisualIds = (
  abilities: Record<string, RaidPlanEncounterAssignmentAbility> | undefined,
  idMap: Record<number, number>,
) => {
  if (!abilities) return
  Object.values(abilities).forEach((ability) => {
    if (ability.visual_id === undefined) return
    const nextId = idMap[ability.visual_id]
    if (nextId === undefined) return
    ability.visual_id = nextId
  })
}

export const remapTimelineVisualIds = (
  timeline: RaidPlanTimeline,
  idMap: Record<number, number>,
): RaidPlanTimeline => {
  const next = cloneTimeline(timeline)
  remapAbilityVisualIds(next.encounter_assignments?.abilities, idMap)
  remapAbilityVisualIds(next.encounter_assignments?.default_snapshots, idMap)
  return next
}

export const destOwnerMatchesVisual = (
  destOwner: RaidPlanTransferDestOwner,
  visual: Pick<RaidPlanTransferVisual, 'ownerType' | 'ownerId'>,
) =>
  destOwner.ownerType === visual.ownerType &&
  destOwner.ownerId === visual.ownerId

export const toRaidPlanApiOwner = (
  record: Pick<RaidPlanTransferVisual, 'ownerType' | 'ownerId'>,
): RaidPlanOwner =>
  record.ownerType === 'User'
    ? { ownerType: 'user' }
    : { ownerType: 'team', ownerId: record.ownerId }

export const toRaidPlanDestOwner = (
  record: Pick<RaidPlanTransferVisual, 'ownerType' | 'ownerId'>,
): RaidPlanTransferDestOwner => ({
  ownerType: record.ownerType,
  ownerId: record.ownerId,
})

export const resolveImportedVisualIdMap = async ({
  owner,
  destOwner,
  encounterId,
  difficulty,
  visualPlans,
  getExisting = getVisualPlanIfPresent,
  create = createVisualPlan,
}: {
  owner: RaidPlanOwner
  destOwner: RaidPlanTransferDestOwner
  encounterId: number
  difficulty: Difficulty
  visualPlans: RaidPlanTransferVisual[]
  getExisting?: VisualPlanLookup
  create?: VisualPlanCreate
}): Promise<Record<number, number>> => {
  const idMap: Record<number, number> = {}

  for (const visual of visualPlans) {
    if (destOwnerMatchesVisual(destOwner, visual)) {
      const existing = await getExisting(owner, visual.id)
      if (existing) {
        idMap[visual.id] = visual.id
        continue
      }
    }

    const created = await create(owner, {
      encounter_id: encounterId,
      difficulty,
      name: visual.name,
      screens: visual.screens,
    })
    idMap[visual.id] = created.id
  }

  return idMap
}

export const resolveImportedRaidPlanTimeline = async ({
  owner,
  destOwner,
  encounterId,
  difficulty,
  timeline,
  visualPlans,
  getExisting,
  create,
}: {
  owner: RaidPlanOwner
  destOwner: RaidPlanTransferDestOwner
  encounterId: number
  difficulty: Difficulty
  timeline: RaidPlanTimeline
  visualPlans: RaidPlanTransferVisual[]
  getExisting?: VisualPlanLookup
  create?: VisualPlanCreate
}): Promise<RaidPlanTimeline> => {
  if (visualPlans.length === 0) return cloneTimeline(timeline)
  const idMap = await resolveImportedVisualIdMap({
    owner,
    destOwner,
    encounterId,
    difficulty,
    visualPlans,
    getExisting,
    create,
  })
  return remapTimelineVisualIds(timeline, idMap)
}

export const loadLinkedVisualPlansForExport = async (
  owner: RaidPlanOwner,
  timeline: RaidPlanTimeline,
  onMissing?: (visualId: number) => void,
  getExisting: VisualPlanLookup = getVisualPlanIfPresent,
): Promise<RaidPlanTransferVisual[]> => {
  const loaded: RaidPlanTransferVisual[] = []
  await Promise.all(
    collectLinkedVisualIds(timeline).map(async (visualId) => {
      const visual = await getExisting(owner, visualId)
      if (!visual) {
        onMissing?.(visualId)
        return
      }
      loaded.push(toTransferVisual(visual))
    }),
  )
  return loaded
}

export const resolveCopiedRaidPlanTimeline = async ({
  source,
  dest,
  onMissingVisual,
  getExisting,
  create,
}: {
  source: Pick<RaidPlanTransferVisual, 'ownerType' | 'ownerId'> & {
    timeline: RaidPlanTimeline
  }
  dest: Pick<
    RaidPlanTransferVisual,
    'ownerType' | 'ownerId' | 'encounterId' | 'difficulty'
  >
  onMissingVisual?: (visualId: number) => void
  getExisting?: VisualPlanLookup
  create?: VisualPlanCreate
}): Promise<RaidPlanTimeline> => {
  const visualPlans = await loadLinkedVisualPlansForExport(
    toRaidPlanApiOwner(source),
    source.timeline,
    onMissingVisual,
    getExisting ?? getVisualPlanIfPresent,
  )
  return resolveImportedRaidPlanTimeline({
    owner: toRaidPlanApiOwner(dest),
    destOwner: toRaidPlanDestOwner(dest),
    encounterId: dest.encounterId,
    difficulty: dest.difficulty,
    timeline: source.timeline,
    visualPlans,
    getExisting,
    create,
  })
}
